/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { z } from 'zod';

import { EmailSchema } from './primitives';

/** ISO `YYYY-MM-DD` calendar date (matches the API's `@IsDateString` + `date`). */
const IsoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a valid date')
  .optional();

const OptionalPhoneSchema = z
  .string()
  .trim()
  .regex(/^\+?[0-9 ()-]{7,20}$/, 'Enter a valid phone number')
  .optional()
  .or(z.literal(''));

const MIN_NAME_ERROR = 'Name is too short';

/**
 * Conditions a paediatric-therapy clinic treats (BRD §M01/§M07 — autism, ADHD,
 * cerebral palsy, speech delay, …). Backs the Admin → Add Client diagnosis
 * picker; `Other` keeps the field open for anything not listed.
 */
export const CLIENT_DIAGNOSES = [
  'Autism Spectrum Disorder',
  'ADHD',
  'Speech & Language Delay',
  'Cerebral Palsy',
  'Intellectual Disability',
  'Global Developmental Delay',
  'Sensory Processing Disorder',
  'Learning Disability',
  'Down Syndrome',
  'Other',
] as const;
export type ClientDiagnosis = (typeof CLIENT_DIAGNOSES)[number];

/**
 * Add a client (Figma "Add New Client": Full Name, Age, Diagnosis, Email, Phone,
 * Assigned Therapist). Mirrors `CreateClientDto` on the API, so the same shape
 * validates on both ends. `age` comes in as a string from the number input and
 * is coerced; empty optionals are dropped before submit.
 */
export const CreateClientSchema = z.object({
  fullName: z.string().trim().min(2, MIN_NAME_ERROR).max(120),
  age: z.coerce.number().int('Enter a whole number').min(0).max(120).optional(),
  primaryDiagnosis: z.string().trim().max(160).optional(),
  /** Parent/guardian name (Figma client form) — stored on the child record. */
  guardianName: z.string().trim().max(120).optional().or(z.literal('')),
  email: z.string().trim().toLowerCase().email('Enter a valid email').optional().or(z.literal('')),
  phone: OptionalPhoneSchema,
  /** Planned therapy sessions per week (Figma patient details). */
  sessionsPerWeek: z.coerce.number().int('Enter a whole number').min(0).max(40).optional(),
  assignedTherapistId: z.string().uuid().optional(),
  familyId: z.string().uuid().optional(),
});
export type CreateClientInput = z.infer<typeof CreateClientSchema>;

/**
 * Add a therapist (Figma "Add Therapist"). Mirrors `CreateTherapistDto`.
 * `specialisations` is entered as free-form tags.
 */
/**
 * Therapist engagement model (BRD v2.0 CR-003). `on_roll` = salaried employee
 * (fixed monthly base + session variable); `consultant` = per-session, paid via
 * their own monthly invoice. Mirrors the API `therapist_category` enum.
 */
export const THERAPIST_CATEGORIES = ['on_roll', 'consultant'] as const;
export type TherapistCategory = (typeof THERAPIST_CATEGORIES)[number];

/** Pay rates are integer paise (Money rule); ₹0–₹1,00,000 per session, ₹0–₹50,00,000 monthly. */
const PerSessionRatePaiseSchema = z.coerce
  .number({ invalid_type_error: 'Enter an amount' })
  .int('Enter a whole rupee amount')
  .min(0)
  .max(10_000_000, 'Rate is too high')
  .optional();
const MonthlyFixedPaiseSchema = z.coerce
  .number({ invalid_type_error: 'Enter an amount' })
  .int('Enter a whole rupee amount')
  .min(0)
  .max(500_000_000, 'Amount is too high')
  .optional();

const therapistFields = {
  fullName: z.string().trim().min(2, MIN_NAME_ERROR).max(120),
  email: EmailSchema,
  phone: OptionalPhoneSchema,
  specialisations: z.array(z.string().trim().min(1).max(48)).max(20).optional(),
  joinedOn: IsoDateSchema,
  category: z.enum(THERAPIST_CATEGORIES).default('on_roll'),
  perSessionRatePaise: PerSessionRatePaiseSchema,
  monthlyFixedPaise: MonthlyFixedPaiseSchema,
} as const;

/** A consultant must have a per-session rate (their invoice is built from it). */
const requireConsultantRate = (
  data: { category?: TherapistCategory | undefined; perSessionRatePaise?: number | undefined },
  ctx: z.RefinementCtx,
): void => {
  if (
    data.category === 'consultant' &&
    (data.perSessionRatePaise === undefined || data.perSessionRatePaise <= 0)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['perSessionRatePaise'],
      message: 'A consultant needs a per-session rate',
    });
  }
};

export const CreateTherapistSchema = z.object(therapistFields).superRefine(requireConsultantRate);
export type CreateTherapistInput = z.infer<typeof CreateTherapistSchema>;

/** Edit a therapist (Admin → Therapist Profile → Edit). Same fields, all optional. */
export const UpdateTherapistSchema = z
  .object(therapistFields)
  .partial()
  .superRefine(requireConsultantRate);
export type UpdateTherapistInput = z.infer<typeof UpdateTherapistSchema>;

/** Document classes an admin attaches to a therapist (BRD v2.0 CR-012). */
export const THERAPIST_DOCUMENT_TYPES = [
  'contract',
  'engagement_letter',
  'cpd_certificate',
  'id_proof',
  'policy',
  'other',
] as const;
export type TherapistDocumentType = (typeof THERAPIST_DOCUMENT_TYPES)[number];

/**
 * Upload a therapist document (Admin → Therapist Profile → Documents). The file
 * is a base64 data URL (PDF or image) — same transport as the intake e-signature
 * — capped to stay within the API body limit. Mirrors `UploadTherapistDocumentDto`.
 */
export const UploadTherapistDocumentSchema = z.object({
  docType: z.enum(THERAPIST_DOCUMENT_TYPES),
  title: z.string().trim().min(1, 'A title is required').max(160),
  fileName: z.string().trim().min(1).max(200),
  dataUrl: z
    .string()
    .regex(/^data:[-\w.+]+\/[-\w.+]+;base64,[A-Za-z0-9+/]+={0,2}$/, 'Attach a PDF or image')
    .max(4_000_000, 'File is too large (about 3 MB max)'),
});
export type UploadTherapistDocumentInput = z.infer<typeof UploadTherapistDocumentSchema>;

/**
 * Invite a parent for a child (Admin → Clients → "Invite Parent"). Creates the
 * family + an invited PARENT login that activates via the set-password link.
 */
export const InviteParentSchema = z.object({
  fullName: z.string().trim().min(2, MIN_NAME_ERROR).max(120),
  email: EmailSchema,
});
export type InviteParentInput = z.infer<typeof InviteParentSchema>;

/**
 * Clinic staff roles an admin can invite (P1-10). PARENT is excluded — parents
 * are invited per-child through the client flow, not the team screen.
 */
export const CLINIC_USER_ROLES = ['CLINIC_ADMIN', 'THERAPIST', 'FINANCE'] as const;
export type ClinicUserRole = (typeof CLINIC_USER_ROLES)[number];

/** Invite a clinic staff member (Admin → Users). Mirrors `InviteClinicUserDto`. */
export const InviteClinicUserSchema = z.object({
  fullName: z.string().trim().min(2, MIN_NAME_ERROR).max(120),
  email: EmailSchema,
  role: z.enum(CLINIC_USER_ROLES),
});
export type InviteClinicUserInput = z.infer<typeof InviteClinicUserSchema>;

/** Enable / disable a clinic user (Admin → Users). Mirrors `SetUserStatusDto`. */
export const SetUserStatusSchema = z.object({
  status: z.enum(['active', 'disabled']),
});
export type SetUserStatusInput = z.infer<typeof SetUserStatusSchema>;
